import React from "react";
import { Badge, Card, Space, Tabs, Typography } from "antd";
import { useSearchParams } from "react-router-dom";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { usePermission, usePermissionRequirement, usePermissionScope } from "~/hooks/usePermission";
import { ChatAlertsTable, useChatAlerts } from "~/modules/Dashboard/AppStats/ChatAlerts";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { TeamMemberTaskList, TeamMemberTaskProvider } from "~/modules/Dashboard/TeamMemberTasks";
import { useAppSelector } from "~/store/initializeStore";
import {
  DashboardSection,
  dashboardSectionBadgeTone,
  dashboardSectionLabels,
  getVisibleDashboardSections,
  isDashboardSectionId,
  type DashboardSectionId,
} from "./dashboardSections";
import { useAssignedTrainersTasks } from "./hooks/useAssignedTrainersTasks";
import { useClientsTrainersById } from "./hooks/useClientsTrainersById";
import { useScheduledEvents } from "./hooks/useScheduledEvents";
import { useTraineesWithoutPlan } from "./hooks/useTraineesWithoutPlan";
import { useUnassignedClients } from "./hooks/useUnassignedClients";
import { ScheduledEvents } from "./ScheduledEvents";
import { TrainerTasks } from "./TrainerTasks";
import { UnassignedClientsTable } from "./UnassignedClientsTable";

const TAB_QUERY_PARAM = "tab";

/** Tab title plus the size of its queue, so the work to be done is visible without opening the tab. */
const SectionTabLabel = ({ section, count }: { section: DashboardSectionId; count: number }): React.ReactElement => (
  <Space size={8}>
    {dashboardSectionLabels[section]}
    <Badge
      count={count}
      overflowCount={999}
      color={dashboardSectionBadgeTone[section] === "neutral" ? "blue" : undefined}
    />
  </Space>
);

/**
 * Queues are capped by their query, so the badge (an exact count) can outrun the rows on screen.
 * Say so rather than letting the table look complete.
 */
const SectionBody = ({
  shown,
  total,
  children,
}: {
  shown: number;
  total: number;
  children: React.ReactNode;
}): React.ReactElement => (
  <Space direction="vertical" size={12} className="w-full">
    {total > shown ? (
      <Typography.Text type="secondary">
        Pokazano {shown} z {total}
      </Typography.Text>
    ) : null}
    {children}
  </Space>
);

export const TeamMemberDashboard = (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdministration } = useUserRole();
  const canReadChatAlerts = usePermissionRequirement(dashboardPermissionRequirements.chatAlerts);
  // Kolejki klientek stoją na clients.read, nie na roli — SUPPORT po prostu go
  // nie ma, a nowa rola z tym kluczem nie wymaga poprawki tutaj (TRE-1855).
  const clientsReadScope = usePermissionScope(PermissionKey.CLIENTS_READ);
  const canAssignTrainers = usePermission(PermissionKey.CLIENTS_WRITE, PermissionScope.ALL);
  // Backlog trenerów stoi na przypisaniach (`team_member_assigned`), nie na zakresie
  // klucza — `useAssignedTrainersTasks` pyta dokładnie o nich. Warunek `clients.read
  // === TEAM` zabierał tę zakładkę head coachowi z szerszym zakresem, choć jego
  // trenerzy nigdzie nie znikali (TRE-2212).
  const assignedTrainers = useAppSelector((store) => store.team.assignedTrainers);

  const visibleSections = React.useMemo(
    () =>
      getVisibleDashboardSections({
        isAdministration,
        canReadClients: clientsReadScope !== PermissionScope.NONE,
        coachesATeam: assignedTrainers.length > 0,
        canAssignTrainers,
        canReadChatAlerts,
      }),
    [isAdministration, clientsReadScope, assignedTrainers.length, canAssignTrainers, canReadChatAlerts],
  );
  // Badges have to be right before the tab is opened, so every visible queue loads up front — and a
  // queue this viewer cannot see never issues its query at all.
  const isVisible = (section: DashboardSectionId) => visibleSections.includes(section);

  const chatAlerts = useChatAlerts(isVisible(DashboardSection.CHAT_ALERTS));
  const unassignedClients = useUnassignedClients(isVisible(DashboardSection.UNASSIGNED_CLIENTS));
  const consultations = useScheduledEvents(isVisible(DashboardSection.CONSULTATIONS));
  const clientsWithoutPlan = useTraineesWithoutPlan(isVisible(DashboardSection.CLIENTS_WITHOUT_PLAN));
  const assignedTrainersTasks = useAssignedTrainersTasks(isVisible(DashboardSection.ASSIGNED_TRAINERS_TASKS));
  // "Who is the trainer" only carries information above one's own assignment —
  // a coach's list has one trainer by definition.
  const trainersById = useClientsTrainersById(clientsWithoutPlan.clients, clientsReadScope === PermissionScope.ALL);

  const sections: Record<DashboardSectionId, { count: number; children: React.ReactNode }> = {
    [DashboardSection.CHAT_ALERTS]: {
      count: chatAlerts.data?.count ?? 0,
      children: (
        <ChatAlertsTable
          messages={chatAlerts.data?.messages ?? []}
          isLoading={chatAlerts.isLoading}
          updateMessage={chatAlerts.updateMessage}
          sentiment={chatAlerts.sentiment}
          onSentimentChange={chatAlerts.setSentiment}
          teamScope={chatAlerts.teamScope}
          onTeamScopeChange={chatAlerts.setTeamScope}
          canNarrowToOwnTeam={chatAlerts.canNarrowToOwnTeam}
        />
      ),
    },
    [DashboardSection.UNASSIGNED_CLIENTS]: {
      count: unassignedClients.count,
      children: (
        <SectionBody shown={unassignedClients.clients.length} total={unassignedClients.count}>
          <UnassignedClientsTable clients={unassignedClients.clients} isLoading={unassignedClients.isLoading} />
        </SectionBody>
      ),
    },
    [DashboardSection.CONSULTATIONS]: {
      count: consultations.data?.length ?? 0,
      children: <ScheduledEvents events={consultations.data} isLoading={consultations.isLoading} />,
    },
    [DashboardSection.CLIENTS_WITHOUT_PLAN]: {
      count: clientsWithoutPlan.count,
      children: (
        <SectionBody shown={clientsWithoutPlan.clients.length} total={clientsWithoutPlan.count}>
          <TrainerTasks
            clients={clientsWithoutPlan.clients}
            trainersById={trainersById}
            isLoading={clientsWithoutPlan.isLoading}
          />
        </SectionBody>
      ),
    },
    [DashboardSection.ASSIGNED_TRAINERS_TASKS]: {
      count: assignedTrainersTasks.count,
      children: (
        <TrainerTasks
          clients={assignedTrainersTasks.clients}
          trainersById={assignedTrainersTasks.trainersById}
          isLoading={assignedTrainersTasks.isLoading}
        />
      ),
    },
  };

  const requestedTab = searchParams.get(TAB_QUERY_PARAM);
  // A link to a queue this viewer cannot open falls back to their first tab instead of a blank panel.
  const activeTab = isDashboardSectionId(requestedTab) && isVisible(requestedTab) ? requestedTab : visibleSections[0];

  return (
    <TeamMemberTaskProvider>
      <Space direction="vertical" size={24} className="w-full">
        <TeamMemberTaskList />

        {/* SUPPORT works tickets, not clients — with no queue to show, the tab strip would be an
            empty frame around nothing. */}
        {visibleSections.length > 0 && (
          <Card>
            <Tabs
              type="card"
              hideAdd
              activeKey={activeTab}
              onChange={(key) => setSearchParams({ [TAB_QUERY_PARAM]: key }, { replace: true })}
              items={visibleSections.map((section) => ({
                key: section,
                label: <SectionTabLabel section={section} count={sections[section].count} />,
                children: sections[section].children,
              }))}
            />
          </Card>
        )}
      </Space>
    </TeamMemberTaskProvider>
  );
};
